import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';

/** 새 회기 (UX_FLOW §8): 내담자만 고르면 회기 번호·날짜·기본 시간은 자동 제안 */
export function SessionNewScreen() {
  const svc = useService();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [clientId, setClientId] = useState(params.get('clientId') ?? '');
  const [override, setOverride] = useState<{ n?: string; date?: string; minutes?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { data: clients } = useLoad(() => svc.listClients(), [svc]);
  const { data: suggestion } = useLoad(
    async () => (clientId ? svc.suggestNextSession(clientId) : null),
    [svc, clientId],
  );

  const n = override.n ?? String(suggestion?.sessionNumber ?? '');
  const date = override.date ?? suggestion?.sessionDate ?? '';
  const minutes = override.minutes ?? String(suggestion?.durationMinutes ?? 50);

  async function onStart(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = await svc.startSession(clientId, {
        sessionNumber: Number(n),
        sessionDate: date,
        durationMinutes: Number(minutes),
      });
      navigate(`/sessions/${s.id}`, { replace: true });
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  const active = (clients ?? []).filter((c) => c.status !== 'terminated');
  return (
    <section className="stack narrow-content">
      <h1>새 회기 처리</h1>
      <Link className="prep-link" to={clientId ? `/clients/${clientId}/run` : '/run'}>
        <span className="li-main">
          <span className="li-title">회기 진행 화면에서 하면 더 편해요</span>
          <span className="li-sub">전사 넣기 → 정리 → AI → 검토 → 마무리를 한 화면씩 안내해요</span>
        </span>
        <span className="li-go">→</span>
      </Link>
      <form onSubmit={onStart} className="card">
        <label>
          내담자
          <select
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              setOverride({});
            }}
          >
            <option value="">선택</option>
            {active.map((c) => (
              <option key={c.id} value={c.id}>
                {c.clientCode}
                {c.alias ? ` (${c.alias})` : ''}
              </option>
            ))}
          </select>
        </label>
        {clientId && (
          <>
            <label>
              회기 번호
              <input
                type="number"
                min={1}
                value={n}
                onChange={(e) => setOverride((o) => ({ ...o, n: e.target.value }))}
              />
            </label>
            <label>
              날짜
              <input type="date" value={date} onChange={(e) => setOverride((o) => ({ ...o, date: e.target.value }))} />
            </label>
            <label>
              상담 시간(분)
              <input
                type="number"
                min={1}
                value={minutes}
                onChange={(e) => setOverride((o) => ({ ...o, minutes: e.target.value }))}
              />
              <span className="muted small">기본 50분. 60~70분, 120분 이상도 그대로 입력하세요.</span>
            </label>
          </>
        )}
        {error && (
          <p role="alert" className="warn">
            {error}
          </p>
        )}
        <button type="submit" className="primary" disabled={!clientId || busy || !n || !date}>
          회기 시작
        </button>
      </form>
      {clients && clients.length === 0 && <p className="muted">먼저 내담자를 등록하세요.</p>}
    </section>
  );
}
